import { Button } from "@/components/ui/button";
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ReportSummaryEditor } from "@/components/reports/report-doc-atoms";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { putFetcher } from "@/lib/swr";
import useSWRMutation from "swr/mutation";
import { mutate } from "swr";
import type { Report } from "@/types/domain";

export const SummaryCell = ({
  report,
  summary,
}: {
  report: Report;
  summary?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState(summary || "");
  const { toast } = useToast();
  const { trigger, isMutating } = useSWRMutation(
    `/reports/${report.id}`,
    putFetcher
  );

  const handleSave = async () => {
    try {
      await trigger({ summary: editedSummary });
      toast({
        title: "Saved",
        description: "Report summary updated",
      });
      setIsEditing(false);
      // Refresh list data
      mutate(
        (key) => Array.isArray(key) && key[0] === "/reports",
        undefined,
        { revalidate: true }
      );
    } catch (error) {
      toastError(
        toast,
        "Save failed",
        error,
        "An error occurred while updating the report summary"
      );
    }
  };

  const handleCancel = () => {
    setEditedSummary(summary || "");
    setIsEditing(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="line-clamp-3 h-auto w-full justify-start whitespace-normal p-0 text-left font-normal hover:bg-transparent hover:text-primary"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          {summary || "No summary"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Report summary</DialogTitle>
          <DialogDescription>
            {report.name || "Untitled report"}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex-1 overflow-y-auto">
          {isEditing ? (
            <ReportSummaryEditor
              value={editedSummary}
              onChange={setEditedSummary}
              onCancel={handleCancel}
              onSave={handleSave}
              isSaving={isMutating}
            />
          ) : (
            <div className="flex items-start justify-between">
              <p className="whitespace-pre-wrap break-words text-sm flex-1">
                {summary || "No summary"}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="ml-4"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
              >
                Edit
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
